import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { COST_DATA, WHY_INDIA, TRUST, STEPS, COMPARISON, TESTIMONIALS, HOLISTIC_CARE, money } from "@/lib/medicalData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  MessageSquareHeart, ArrowRight, Stethoscope, Search, Package, ShieldCheck, Star, Quote,
  Heart, UserCheck, HandHeart, UtensilsCrossed, Flower2, Sparkles, Home, Leaf,
  MessageSquare, CalendarCheck, Plane, HeartPulse, Bed, Award, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

const HOLISTIC_ICONS = { Heart, UserCheck, HandHeart, UtensilsCrossed, Flower2, Sparkles, Home, Leaf, ShieldCheck };

const JOURNEY_STEPS = [
  { icon: MessageSquare, title: "Consult", body: "Tell us what you need. Our AI matches you with the best specialists in India." },
  { icon: CalendarCheck, title: "Plan", body: "We coordinate your treatment plan, visa documentation, and travel dates." },
  { icon: Plane, title: "Fly", body: "Book flights through us. Airport pickup arranged. You're never alone." },
  { icon: HeartPulse, title: "Treat", body: "Receive world-class care at NABH/JCI-accredited hospitals — at 80–90% less." },
  { icon: Bed, title: "Recover", body: "Private nursing, home-cooked meals, yoga, Ayurveda — healing the Indian way." },
  { icon: Home, title: "Return Home", body: "Post-treatment follow-ups. Teleconsultations. We stay with you after you're home." },
];
import { Disclaimer } from "@/components/Disclaimers";

function CountUp({ value, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf; const start = performance.now(); const dur = 1100;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return <span ref={ref}>{prefix}{n.toLocaleString()}{suffix}</span>;
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

// Order matches WHY_INDIA in medicalData.js
const WHY_INDIA_IMAGES = [
  { src: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=400&h=200&fit=crop&q=80", alt: "Cost savings visualization" },
  { src: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&h=200&fit=crop&q=80", alt: "World-class hospital facility in India" },
  { src: "https://images.unsplash.com/photo-1504439468489-c8920d796a29?w=400&h=200&fit=crop&q=80", alt: "Doctor providing immediate consultation" },
  { src: "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400&h=200&fit=crop&q=80", alt: "Luxury recovery accommodation in India" },
  { src: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=200&fit=crop&q=80", alt: "English-speaking medical professional" },
  { src: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=400&h=200&fit=crop&q=80", alt: "Taj Mahal — combine healing with travel in India" },
];

const GALLERY = [
  { src: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=600&h=400&fit=crop&q=80", caption: "Taj Mahal, Agra", alt: "Taj Mahal at sunrise, Agra, India" },
  { src: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=600&h=500&fit=crop&q=80", caption: "Kerala Backwaters", alt: "Traditional houseboat on Kerala backwaters" },
  { src: "https://images.unsplash.com/photo-1477587458883-47145ed94245?w=600&h=350&fit=crop&q=80", caption: "Himalayan Wellness Retreats", alt: "Snow-capped Himalayan mountains" },
  { src: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=600&h=400&fit=crop&q=80", caption: "Goa Beach Recovery", alt: "Beautiful beach in Goa, India" },
  { src: "https://images.unsplash.com/photo-1587474260584-136574528ed5?w=600&h=450&fit=crop&q=80", caption: "Delhi — Gateway to Healthcare", alt: "India Gate monument in New Delhi" },
  { src: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=600&h=400&fit=crop&q=80", caption: "Mumbai — Medical Hub", alt: "Gateway of India, Mumbai" },
  { src: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&h=500&fit=crop&q=80", caption: "Rajasthan Heritage", alt: "Colorful palace in Rajasthan" },
  { src: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&h=350&fit=crop&q=80", caption: "Luxury Recovery Stays", alt: "Luxury villa with pool for recovery stays" },
  { src: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&h=400&fit=crop&q=80", caption: "Yoga & Meditation", alt: "Person practicing yoga at sunrise" },
];

const AVATARS = {
  "Sarah M.": "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=96&h=96&fit=crop&q=80",
  "James K.": "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=96&h=96&fit=crop&q=80",
  "Maria L.": "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=96&h=96&fit=crop&q=80",
};

// Elegant saffron-white-green underline (Indian tricolor) — used in exactly 2 places on Landing.
function TricolorUnderline() {
  return (
    <div className="mx-auto mt-4 flex h-1 w-20 overflow-hidden rounded-full" data-testid="tricolor-underline">
      <span className="flex-1 bg-[#FF9933]" />
      <span className="flex-1 bg-white" />
      <span className="flex-1 bg-[#138808]" />
    </div>
  );
}

const HOSPITAL_PARTNERS = [
  "Apollo Hospitals", "Fortis Healthcare", "Manipal Hospitals", "Medanta – The Medicity",
  "Narayana Health", "Yashoda Hospitals", "Aster DM Healthcare", "MIOT International",
  "Lilavati Hospital", "Ruby Hall Clinic", "CIMS Hospital", "KIMS Hospitals",
  "Amrita Hospital", "Gleneagles Global", "Wockhardt Hospitals", "CARE Hospitals",
  "HCG Cancer Centre", "Fortis Escorts", "AMRI Hospitals", "Sakra World Hospital",
  "Shalby Hospitals", "NIMHANS", "Aravind Eye Hospital", "Indian Spinal Injuries Centre",
  "BLK-Max Super Speciality", "Sir Ganga Ram Hospital", "Jaypee Hospital", "Artemis Hospital",
  "P.D. Hinduja Hospital", "Jaslok Hospital", "Nanavati Max", "Kauvery Hospital",
  "Rela Hospital", "Continental Hospitals", "AIG Hospitals", "Medica Superspecialty",
  "Sankara Nethralaya", "LV Prasad Eye Institute", "Sparsh Hospital", "Paras Hospitals",
  "Tata Memorial Hospital", "CMC Vellore", "Aster Medcity", "KMCH Coimbatore",
  "Ruby Hall Clinic", "Zydus Hospital", "Medanta Lucknow", "Fortis Mohali",
];

function HospitalMarquee() {
  const row = [...HOSPITAL_PARTNERS, ...HOSPITAL_PARTNERS];
  return (
    <section className="bg-white border-y border-[#E4E7EC] py-8 overflow-hidden" data-testid="trusted-partners-strip">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-[#98A2B3]">Trusted Hospital Partners</p>
      </div>
      <div className="relative mt-5 flex overflow-hidden">
        <motion.div
          className="flex shrink-0 items-center gap-3 pr-3"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 40, ease: "linear", repeat: Infinity }}>
          {row.map((name, i) => (
            <span key={i}
              className="shrink-0 rounded-full border border-[#E4E7EC] bg-[#F7F9FC] px-5 py-2 text-sm font-medium text-[#475467] whitespace-nowrap hover:border-[#0D5C75] hover:text-[#0D5C75] transition-colors duration-200">
              {name}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}




export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [calc, setCalc] = useState(COST_DATA[0]);
  const [email, setEmail] = useState("");
  const [stats, setStats] = useState({ hospitals_display: "80+", cities_display: "15+" });

  useEffect(() => {
    api.get("/v1/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/v1/leads", { email, source: "landing" });
      toast.success("Thank you! We'll be in touch.");
      setEmail("");
    } catch { toast.error("Something went wrong. Please try again."); }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden text-white">
        <img
          src="https://images.unsplash.com/photo-1551076805-e1869033e561?w=1920&h=900&fit=crop&q=80"
          alt="Modern hospital corridor representing world-class healthcare in India"
          loading="eager" fetchPriority="high" decoding="async"
          className="absolute inset-0 h-full w-full object-cover object-center bg-[#F3F4F6]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D5C75]/95 via-[#134E5E]/82 to-[#2A9D8F]/55" />
        <div className="absolute inset-0 opacity-10 mv-grain" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 min-h-[88vh] lg:min-h-screen relative grid lg:grid-cols-2 gap-10 items-center content-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 rounded-full bg-black/45 border border-white/20 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md" data-testid="hero-hospitals-badge">
              <Award size={15} className="text-[#FBBF24]" /> {stats.hospitals_display} Partner Hospitals Across {stats.cities_display} Indian Cities
            </span>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
              World-Class Treatment in India —<br /><span className="text-[#FBBF24]">We Handle Everything.</span>
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-xl">
              From your first inquiry to your full recovery — consultation, visa, flights, hospital, stay, and aftercare. One platform. Zero stress.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="mv-pulse rounded-full h-12 px-7 bg-[#D97706] text-white hover:bg-[#b45f05] hover:text-white"
                onClick={() => navigate("/services")} data-testid="hero-find-treatment-btn">
                Find Your Treatment <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full h-12 px-7 bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white"
                onClick={() => navigate(user ? "/assistant" : "/register")} data-testid="hero-ai-btn">
                <MessageSquareHeart size={20} className="mr-2" /> Talk to Our AI Assistant
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-white">
              <span className="flex items-center gap-1.5 bg-black/35 px-3.5 py-1.5 rounded-lg backdrop-blur-sm"><ShieldCheck size={16} className="text-[#FBBF24]" /> JCI / NABH accredited</span>
              <span className="flex items-center gap-1.5 bg-black/35 px-3.5 py-1.5 rounded-lg backdrop-blur-sm"><ShieldCheck size={16} className="text-[#FBBF24]" /> Escrow-protected payments</span>
            </div>
          </motion.div>

          {/* Cost calculator */}
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={1}
            className="tricolor-top bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-[#101828]" data-testid="cost-calculator">
            <div className="flex items-center gap-2 text-[#0D5C75] font-heading font-bold text-lg">
              <Search size={20} /> Cost Savings Calculator
            </div>
            <p className="text-sm text-muted-foreground mt-1">See how much you could save.</p>
            <div className="mt-4">
              <Select value={calc.key} onValueChange={(k) => setCalc(COST_DATA.find((c) => c.key === k))}>
                <SelectTrigger className="w-full" data-testid="calc-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COST_DATA.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-[#E4E7EC] p-4">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">USA Price</div>
                <div className="font-heading text-2xl sm:text-3xl font-extrabold text-[#101828] mt-1 line-through decoration-[#F97316]/60">
                  <CountUp key={"usa" + calc.key} value={calc.usa} prefix="$" />
                </div>
              </div>
              <div className="rounded-xl border-2 border-[#10B981] bg-[#D1FAE5]/40 p-4">
                <div className="text-xs text-[#0D5C75] uppercase tracking-wide font-semibold">India Price</div>
                <div className="font-heading text-3xl sm:text-4xl font-extrabold text-[#10B981] mt-1">
                  <CountUp key={"india" + calc.key} value={calc.india} prefix="$" />
                </div>
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-[#0D5C75] text-white p-4 flex items-center justify-between">
              <span className="text-sm text-white/85">You save</span>
              <span className="font-heading text-3xl font-extrabold text-[#FBBF24]" data-testid="calc-savings">
                <CountUp key={"sav" + calc.key} value={calc.savings} suffix="%" />
              </span>
            </div>
            <Disclaimer testid="pricing-disclaimer" className="mt-4">
              Prices shown are estimates based on published hospital rates and may vary based on individual case complexity, diagnostic findings, and treatment plan. Final pricing is confirmed by your chosen provider after medical evaluation.
            </Disclaimer>
            <p className="mt-1.5 text-xs text-muted-foreground/80" data-testid="pricing-date-stamp">
              Estimates current as of June 2026. Not a binding quote.
            </p>
          </motion.div>
        </div>
        <button onClick={() => window.scrollTo({ top: window.innerHeight * 0.88, behavior: "smooth" })}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-1 text-white/90 [text-shadow:0_1px_4px_rgba(0,0,0,0.5)] hover:text-white transition-colors"
          data-testid="hero-scroll-down" aria-label="Scroll down">
          <span className="text-[11px] uppercase tracking-[0.2em]">Scroll</span>
          <ChevronDown size={22} className="mv-bounce-down" />
        </button>
      </section>

      {/* TRUSTED HOSPITAL PARTNERS — scrolling pill strip */}
      <HospitalMarquee />

      {/* JOURNEY — END TO END, DOOR TO DOOR */}
      <section className="bg-white" data-testid="journey-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#101828] text-center">Your Journey With SarvaSeva — End to End, Door to Door</h2>
          <p className="text-center text-muted-foreground mt-3">सर्व सेवा — Complete Service at Every Step</p>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-10 gap-x-4">
            {JOURNEY_STEPS.map((s, i) => (
              <motion.div key={s.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="relative flex flex-col items-center text-center px-2" data-testid={`journey-step-${i}`}>
                {i < JOURNEY_STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-7 left-1/2 w-full h-0.5 bg-gradient-to-r from-[#0D5C75]/40 to-[#2A9D8F]/10" />
                )}
                <span className="relative z-10 grid place-items-center h-14 w-14 rounded-2xl bg-[#0D5C75] text-white shadow-md">
                  <s.icon size={26} />
                </span>
                <div className="mt-4 flex items-center gap-1.5">
                  <span className="font-heading text-xs font-bold text-[#2A9D8F]">{i + 1}</span>
                  <h3 className="font-heading font-bold text-[#101828]">{s.title}</h3>
                </div>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 rounded-2xl bg-[#FFF7ED] border border-[#F5D9B0]/50 px-6 py-7 text-center" data-testid="journey-callout">
            <p className="font-heading font-bold text-[20px] sm:text-[22px] text-[#101828] leading-snug max-w-3xl mx-auto">
              Other platforms connect you to a hospital. SarvaSeva walks with you from your front door — and brings you safely back.
            </p>
          </div>
        </div>
      </section>

      {/* WHY INDIA */}
      <section className="bg-[#FFF7ED]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#101828] text-center">Why India?</h2>
          <TricolorUnderline />
          <p className="text-center text-muted-foreground mt-3 max-w-2xl mx-auto">The world's fastest-growing medical tourism destination — trusted by hundreds of thousands of patients every year.</p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_INDIA.map((c, i) => (
              <motion.div key={c.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="card-modern overflow-hidden border-t-4"
                style={{ borderTopColor: c.border }} data-testid={`why-india-card-${i}`}>
                <img src={WHY_INDIA_IMAGES[i]?.src} alt={WHY_INDIA_IMAGES[i]?.alt || c.title}
                  loading="lazy" decoding="async" className="h-40 w-full object-cover bg-[#F3F4F6]" />
                <div className="p-6">
                  <h3 className="font-heading font-bold text-lg text-[#101828]">{c.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="tricolor-top mv-gradient-hero text-white" data-testid="trust-signals-bar"
        style={{ borderTop: "3px solid", borderImage: "linear-gradient(to right, #FF9933 33%, #FFFFFF 33% 66%, #138808 66%) 1" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {TRUST.map((t) => (
            <div key={t.label} data-testid={`trust-${t.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="font-heading text-3xl sm:text-4xl font-extrabold text-[#FBBF24]">{t.value}</div>
              <div className="text-sm text-white/80 mt-1">{t.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <span className="section-overline mx-auto mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#101828] text-center">How It Works</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[Stethoscope, Search, Package, ShieldCheck].map((Icon, i) => (
              <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="relative card-modern p-6" data-testid={`step-card-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#0D5C75] text-white"><Icon size={22} /></span>
                  <span className="font-heading text-3xl font-extrabold text-[#0D5C75]/30">{i + 1}</span>
                </div>
                <h3 className="font-heading font-bold text-lg text-[#101828] mt-4">{STEPS[i].title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{STEPS[i].body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOLISTIC CARE ECOSYSTEM */}
      <section className="bg-[#FFF7ED] relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1545389336-cf090694435e?w=1920&h=600&fit=crop&q=80" alt=""
          aria-hidden="true" loading="lazy" decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-[0.12] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 relative">
          <span className="section-overline mx-auto mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#101828] text-center">What You Won't Find in Western Hospitals</h2>
          <p className="text-center text-muted-foreground mt-3 max-w-2xl mx-auto">In India, healing is not just a procedure — it's a complete care experience.</p>

          <div className="mt-12 flex gap-6 overflow-x-auto pb-4 snap-x lg:grid lg:grid-cols-3 lg:overflow-visible lg:pb-0" data-testid="holistic-care-grid">
            {HOLISTIC_CARE.map((c, i) => {
              const Icon = HOLISTIC_ICONS[c.icon] || Heart;
              return (
                <motion.div key={c.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i % 3}
                  className="shrink-0 w-[280px] lg:w-auto snap-start card-modern p-6 border-t-4"
                  style={{ borderTopColor: c.accent }} data-testid={`holistic-card-${i}`}>
                  <span className="grid place-items-center h-12 w-12 rounded-xl text-white mb-4" style={{ backgroundColor: c.accent }}>
                    <Icon size={24} />
                  </span>
                  <h3 className="font-heading font-bold text-lg text-[#101828]">{c.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-8 rounded-xl bg-gradient-to-r from-[#0D5C75] to-[#2A9D8F] text-white text-center font-heading font-bold text-[22px] leading-snug px-6 py-6"
            data-testid="holistic-callout">
            In the USA, you're discharged and sent home with a pamphlet. In India, you're cared for like family.
          </div>
        </div>
      </section>

      {/* B9CONCEPT FEATURED PARTNER CALLOUT */}
      <section className="bg-gradient-to-b from-[#F0F9FF] to-white" data-testid="b9-callout-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#D97706] text-white text-xs font-bold px-4 py-1.5" data-testid="b9-callout-badge">
              <Award size={14} /> FEATURED PARTNER
            </span>
          </div>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-bold text-[#101828] text-center max-w-3xl mx-auto">
            Chronic Illness? It's Not "All in Your Head" — It's in Your Trauma.
          </h2>
          <p className="mt-4 text-center text-muted-foreground max-w-2xl mx-auto">
            B9Concept's revolutionary protocol resolves 137+ chronic conditions permanently — without medication. Now available through SarvaSeva at centers in Nashik and Noida.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {[
              { stat: "137+", label: "Chronic Conditions Addressed" },
              { stat: "10,000+", label: "Lives Transformed Globally" },
              { stat: "Same-Day", label: "Resolution Protocol" },
            ].map((c) => (
              <div key={c.label} className="rounded-2xl bg-white border border-[#FDE68A] shadow-sm text-center px-4 py-6" data-testid={`b9-stat-${c.stat.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}>
                <div className="font-heading font-extrabold text-3xl text-[#D97706]">{c.stat}</div>
                <div className="mt-1.5 text-sm text-muted-foreground">{c.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Button onClick={() => navigate("/services?focus=b9")}
              className="rounded-full h-12 px-8 bg-[#D97706] text-white hover:bg-[#B45309] hover:text-white text-base"
              data-testid="b9-discover-btn">
              Discover B9Concept <ArrowRight size={18} className="ml-1.5" />
            </Button>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-[#E9F5F9]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#101828] text-center">India vs. USA / Europe</h2>
          <div className="mt-10 overflow-x-auto rounded-2xl border border-[#E4E7EC] bg-white shadow-md">
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead>
                <tr className="bg-[#0D5C75] text-white text-left">
                  <th className="p-4 font-heading">Factor</th>
                  <th className="tricolor-top p-4 font-heading">India</th>
                  <th className="p-4 font-heading">USA</th>
                  <th className="p-4 font-heading">Europe</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((r, i) => (
                  <tr key={r.factor} className={i % 2 ? "bg-[#F7F9FC]" : "bg-white"}>
                    <td className="p-4 font-medium text-[#101828]">{r.factor}</td>
                    <td className="p-4 bg-[#D1FAE5]/50 font-semibold text-[#087443]">{r.india}</td>
                    <td className="p-4 text-muted-foreground">{r.usa}</td>
                    <td className="p-4 text-muted-foreground">{r.europe}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <span className="section-overline mx-auto mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#101828] text-center">Patients Who Made the Journey</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={t.name} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="rounded-2xl bg-[#FFF7ED] border border-[#F5D9B0]/40 p-6 relative" data-testid={`testimonial-${i}`}>
                <Quote size={40} className="text-[#D97706]/25 absolute top-4 right-4" />
                <div className="flex items-center gap-1 text-[#D97706]">
                  {Array.from({ length: t.rating }).map((_, k) => <Star key={k} size={16} fill="#D97706" />)}
                </div>
                <p className="mt-4 text-sm text-[#101828] leading-relaxed">"{t.quote}"</p>
                <div className="mt-5 flex items-center gap-3">
                  <img src={AVATARS[t.name]} alt={t.name} loading="lazy" decoding="async"
                    className="h-12 w-12 rounded-full object-cover bg-[#F3F4F6] shrink-0" />
                  <div>
                    <div className="font-semibold text-sm text-[#101828] flex items-center gap-1.5">{t.name} <span className="text-lg">{t.flag}</span></div>
                    <div className="text-xs text-muted-foreground">{t.procedure} · {t.country}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <Disclaimer testid="testimonial-disclaimer" className="mt-6 justify-center text-center max-w-2xl mx-auto">
            Testimonials reflect individual patient experiences. Results, savings, and timelines may vary based on medical condition, treatment complexity, and other individual factors.
          </Disclaimer>
        </div>
      </section>

      {/* INDIA TOURISM GALLERY */}
      <section className="bg-white" data-testid="tourism-gallery-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#101828] text-center">Your Healing Journey Through India</h2>
          <TricolorUnderline />
          <p className="text-center text-muted-foreground mt-3 max-w-2xl mx-auto">Recover in a land of breathtaking beauty, rich culture, and warm hospitality</p>

          <div className="mt-12 columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
            {GALLERY.map((g, i) => (
              <div key={g.src} className="mb-4 break-inside-avoid relative overflow-hidden rounded-2xl group shadow-md" data-testid={`gallery-image-${i}`}>
                <img src={g.src} alt={g.alt} loading="lazy" decoding="async"
                  className="w-full object-cover bg-[#F3F4F6] transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <span className="text-white font-heading font-semibold text-sm drop-shadow">{g.caption}</span>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-muted-foreground max-w-3xl mx-auto">
            India offers 40+ JCI-accredited hospitals across vibrant cities — from Delhi's world-class Medanta to Kerala's Ayurvedic heritage centers.
          </p>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="tricolor-top mv-gradient-hero text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">Your Healing Journey Starts Here</h2>
          <p className="mt-3 text-white/80">Drop your email and our care team will help you plan every step.</p>
          <form onSubmit={submitLead} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com" className="h-12 bg-white text-[#101828]" data-testid="lead-email-input" />
            <Button type="submit" className="h-12 rounded-full bg-[#D97706] text-white hover:bg-[#b45f05] hover:text-white px-7 shrink-0" data-testid="lead-submit-btn">
              Get Started
            </Button>
          </form>
        </div>
      </section>

      <footer className="tricolor-top bg-[#0D5C75] text-white/70 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row justify-between gap-4 text-sm">
          <span className="font-heading font-bold text-white" data-testid="landing-footer-brand">SarvaSeva — सर्व सेवा — Complete Service, From Consultation to Recovery</span>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <button onClick={() => navigate("/services")} className="hover:text-white">About</button>
            <button onClick={() => navigate("/services")} className="hover:text-white">How It Works</button>
            <button onClick={() => navigate("/register")} className="hover:text-white">For Providers</button>
            <span className="hover:text-white cursor-default">Contact</span>
            <span className="hover:text-white cursor-default">Privacy Policy</span>
          </div>
        </div>
        <div className="text-center text-xs text-white/50 pb-6">Guidance-only AI · Not a substitute for professional medical advice.</div>
      </footer>
    </div>
  );
}
